import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { colors } from '../../config/styles';
import SvgIcon from '../Icon/SvgIcon';
import KubitTooltip from '../KubitTooltip';
import { theme } from '../../styles/theme';
import { formInputStyles } from '../../styles/shareableStyles';

const Container = styled.div`
  width: 100%;
  position: relative;
`;

const TextArea = styled.textarea<{ $hasError: boolean }>`
  width: 100%;
  padding: 8px;
  resize: none;
  font: inherit;
  outline: none;
  ${formInputStyles};
  &:hover,
  &:focus {
    border-color: ${theme.colors.mainBlue};
  }

  ${({ $hasError }) =>
    $hasError &&
    css`
      border-color: ${colors.mainError};
    `};
`;

const ErrorIcon = styled(SvgIcon)<{ $rightOffset: number }>`
  position: absolute;
  top: 6px;
  right: ${({ $rightOffset }) => $rightOffset}px;
`;

interface IProps {
  defaultRows?: number;
  placeholder: string;
  value: string;
  hasError?: boolean;
  errorMessage?: string;
  onChange: (newValue: string) => void;
  onBlur: () => void;
  onSubmit: () => void;
  hasVerticalScroll: boolean;
  autoFocus?: boolean;
}

export default forwardRef<HTMLTextAreaElement, IProps>(
  (
    {
      defaultRows = 4,
      placeholder,
      value,
      hasError = false,
      errorMessage,
      onChange,
      onBlur,
      onSubmit,
      hasVerticalScroll,
      autoFocus,
    },
    ref,
  ) => {
    const handleKeyPress = (
      event: React.KeyboardEvent<HTMLTextAreaElement>,
    ) => {
      if (event.key === 'Enter' && !hasError) {
        onSubmit();
      }
    };

    const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(event.target.value);
    };

    return (
      <Container>
        <TextArea
          ref={ref}
          autoFocus={autoFocus}
          value={value}
          onChange={handleInput}
          onBlur={onBlur}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          rows={defaultRows}
          $hasError={hasError}
        />
        {hasError && errorMessage && (
          <KubitTooltip content={errorMessage} noOpenDelay={true}>
            <ErrorIcon
              name="error-icon"
              width={12}
              height={12}
              $rightOffset={hasVerticalScroll ? 16 : 6}
            />
          </KubitTooltip>
        )}
      </Container>
    );
  },
);
